/* eslint-disable @typescript-eslint/no-explicit-any */
import { ParamConfig, SelectOption } from '@diamond/shared/types';
import { MultiValue, SingleValue } from 'chakra-react-select';
import { useMemo } from 'react';

import { useCleanSearchParams } from './use-clean-searchparams';

/**
 * Type definition for the result of useSelectParams hook
 * Includes original parameter values, update functions for each parameter,
 * and API-ready parameter values
 */
type SelectParamsResult<T extends Record<string, any>> = T & {
  [K in keyof T as `update${Capitalize<string & K>}`]: T[K] extends any[]
    ? (newValue: MultiValue<SelectOption> | null) => void
    : (newValue: SingleValue<SelectOption> | null) => void;
} & {
  apiParams: Record<string, string | string[]>;
};

/**
 * Hook to manage URL-based query parameters for API requests
 * Handles both single-select and multi-select parameters
 *
 * @param config Configuration for parameters (multi-select vs single-select) including optional options array
 * @param options Hook options including resetPage behavior
 * @returns Object with values, update methods, and API-ready parameters
 */
export function useSelectParams<T extends Record<string, any>>(
  config: ParamConfig,
  options: { resetPage?: boolean } = { resetPage: true }
): SelectParamsResult<T> {
  const { searchParams, setParams } = useCleanSearchParams();

  /**
   * Converts an array of string values to SelectOption objects
   * Uses provided options for labels when available
   */
  const toOptionObjectsFromValues = (
    values: string[],
    options?: SelectOption[]
  ) => {
    const findOption = (value: string): SelectOption => {
      const found = options?.find((opt) => opt.value === value);
      return found || { value, label: value };
    };
    return values.map(findOption);
  };

  /**
   * Parses a comma-separated string into an array of SelectOption objects
   */
  const parseMultiValue = (
    raw: string,
    options?: SelectOption[]
  ): SelectOption[] => {
    if (!raw) return [];
    const values = raw.split(',');
    return toOptionObjectsFromValues(values, options);
  };

  /**
   * Extracts parameter values from URL search params
   * Handles both multi-select (array) and single-select (string) parameters
   */
  const paramValues = useMemo(() => {
    const result: Record<string, any> = {};

    Object.entries(config).forEach(
      ([paramName, { isMulti, defaultValue, options }]) => {
        const rawValue = searchParams.get(paramName);

        if (isMulti) {
          const defaults = Array.isArray(defaultValue) ? defaultValue : [];
          if (rawValue !== null) {
            result[paramName] = parseMultiValue(rawValue, options);
          } else {
            result[paramName] = toOptionObjectsFromValues(defaults, options);
          }
        } else {
          const defaultString =
            typeof defaultValue === 'string' ? defaultValue : '';
          result[paramName] = rawValue !== null ? rawValue : defaultString;
        }
      }
    );

    return result;
  }, [searchParams, config]);

  /**
   * Creates update functions for each parameter
   * Each function updates the URL search params when called
   */
  const updateFunctions: Record<string, any> = {};

  Object.entries(config).forEach(([paramName, { isMulti }]) => {
    const fnName = `update${
      paramName.charAt(0).toUpperCase() + paramName.slice(1)
    }`;

    if (isMulti) {
      updateFunctions[fnName] = (newValue: MultiValue<SelectOption> | null) => {
        const paramValue =
          newValue && newValue.length > 0
            ? newValue.map((item) => item.value).join(',')
            : null;

        const params: Record<string, string | null> = {
          [paramName]: paramValue,
        };
        if (options.resetPage) {
          params.page = '1';
        }
        setParams(params);
      };
    } else {
      updateFunctions[fnName] = (
        newValue: SingleValue<SelectOption> | null
      ) => {
        const paramValue = newValue?.value || null;
        const params: Record<string, string | null> = {
          [paramName]: paramValue,
        };
        if (options.resetPage) {
          params.page = '1';
        }
        setParams(params);
      };
    }
  });

  /**
   * Prepares parameters for API requests
   * Converts SelectOption objects to string values or arrays of string values
   */
  const apiParams: Record<string, string | string[]> = {};

  Object.entries(paramValues).forEach(([paramName, value]) => {
    if (Array.isArray(value)) {
      apiParams[paramName] =
        value.length > 0 ? value.map((item) => item.value) : [];
    } else {
      apiParams[paramName] = value || '';
    }
  });

  return {
    ...paramValues,
    ...updateFunctions,
    apiParams,
  } as SelectParamsResult<T>;
}
